// frontend/src/components/PendingComplaints.js
import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  Container,
  Box,
  Typography,
  Grid,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  IconButton,
  Tooltip,
  TablePagination,
  Checkbox,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Divider,
  CircularProgress,
  Chip,
  useTheme,
  useMediaQuery,
  Collapse,
  Card,
  CardContent,
  Stack,
  alpha
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ROWS_PER_PAGE_OPTIONS,
  readPageParam,
  readRowsParam,
  hasActiveFilterParams,
  useFilterParamSync,
  useClampedPage
} from '../../utils/urlFilters';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import GetAppIcon from '@mui/icons-material/GetApp'; // For Excel export
import FileDownloadIcon from '@mui/icons-material/FileDownload'; // For JSON export
import PublishIcon from '@mui/icons-material/Publish'; // For JSON import
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import CallIcon from '@mui/icons-material/Call';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import api from '../../services/api';

const cellStyle = {
  maxWidth: '150px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const getStatusColor = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'new':
      return '#2196f3';
    case 'pending':
      return '#ff9800';
    case 'resolved':
      return '#4caf50';
    default:
      return '#757575';
  }
};

// Query-string keys for every filter on this page. See src/utils/urlFilters.js
// for why the filters live in the URL rather than in component state.
const FILTER_PARAM_KEYS = ['q', 'from', 'to', 'assembly', 'panchayath', 'status', 'category'];

const PendingComplaints = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialParam = (key) => searchParams.get(key) || '';

  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(() => initialParam('q'));
  const [startDate, setStartDate] = useState(() => initialParam('from'));
  const [endDate, setEndDate] = useState(() => initialParam('to'));

  // Dynamic filter states
  const [assemblyFilter, setAssemblyFilter] = useState(() => initialParam('assembly'));
  const [panchayathFilter, setPanchayathFilter] = useState(() => initialParam('panchayath'));
  const [statusFilter, setStatusFilter] = useState(() => initialParam('status')); // Additional filter if needed
  const [categoryFilter, setCategoryFilter] = useState(() => initialParam('category'));

  const [categories, setCategories] = useState([]);
  const [filterAssemblies, setFilterAssemblies] = useState([]);

  const [page, setPage] = useState(() => readPageParam(searchParams));
  const [rowsPerPage, setRowsPerPage] = useState(() => readRowsParam(searchParams));
  const [selectedComplaints, setSelectedComplaints] = useState([]);
  const [showFilters, setShowFilters] = useState(() => hasActiveFilterParams(searchParams, FILTER_PARAM_KEYS));
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Ref for JSON import file input
  const jsonFileInputRef = useRef(null);

  useFilterParamSync(searchParams, setSearchParams, {
    q: searchQuery,
    from: startDate,
    to: endDate,
    assembly: assemblyFilter,
    panchayath: panchayathFilter,
    status: statusFilter,
    category: categoryFilter
  }, page, rowsPerPage);

  // Fetch complaints using the custom API instance
  const fetchComplaints = async () => {
    try {
      const res = await api.get('/complaints');
      setComplaints(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching complaints:', err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComplaints();
  }, []);

  useEffect(() => {
    api.get('/category')
      .then((res) => setCategories(res.data))
      .catch((err) => console.error('Error fetching categories:', err));
  }, []);

  useEffect(() => {
    api.get('/assemblies')
      .then((res) => setFilterAssemblies(res.data))
      .catch((err) => console.error('Error fetching assemblies:', err));
  }, []);

  // Filter complaints: Only include pending complaints plus any additional filters.
  const filteredComplaints = complaints.filter((complaint) => {
    // Only include pending complaints.
    if ((complaint.status || '').toLowerCase() !== 'pending') return false;
    let matches = true;
    if (searchQuery) {
      const searchStr = Object.values(complaint)
        .filter((val) => typeof val === 'string')
        .join(' ')
        .toLowerCase();
      if (!searchStr.includes(searchQuery.toLowerCase())) matches = false;
    }
    if (startDate && new Date(complaint.complaintDate) < new Date(startDate)) matches = false;
    if (endDate && new Date(complaint.complaintDate) > new Date(endDate)) matches = false;
    if (assemblyFilter && complaint.legislativeAssembly !== assemblyFilter) matches = false;
    if (panchayathFilter && (complaint.panchayath || '').toLowerCase() !== panchayathFilter.toLowerCase()) matches = false;
    if (statusFilter && (complaint.status || '').toLowerCase() !== statusFilter.toLowerCase()) matches = false;
    if (categoryFilter && (complaint.category || '').toLowerCase() !== categoryFilter.toLowerCase()) matches = false;
    return matches;
  });

  const sortedComplaints = filteredComplaints.slice().sort((a, b) => new Date(b.complaintDate) - new Date(a.complaintDate));
  const paginatedComplaints = sortedComplaints.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  useClampedPage(loading, sortedComplaints.length, rowsPerPage, page, setPage);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectComplaint = (id, e) => {
    e.stopPropagation();
    setSelectedComplaints(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const currentPageIds = paginatedComplaints.map(c => c._id);
      setSelectedComplaints(prev => [...new Set([...prev, ...currentPageIds])]);
    } else {
      setSelectedComplaints(prev => prev.filter(id => !paginatedComplaints.some(c => c._id === id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedComplaints.length === 0) return;
    if (window.confirm('Are you sure you want to delete the selected complaints?')) {
      const deletePromises = selectedComplaints.map(id =>
        api.delete(`/complaints/${id}`).catch(err => {
          console.error(`Error deleting complaint ${id}:`, err);
          return { id, error: err };
        })
      );
      const results = await Promise.all(deletePromises);
      const failed = results.filter(result => result && result.error);
      if (failed.length > 0) {
        alert(`Failed to delete ${failed.length} complaint(s).`);
      } else {
        alert('Selected complaints deleted successfully!');
        setSelectedComplaints([]);
        fetchComplaints();
      }
    }
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setStartDate('');
    setEndDate('');
    setAssemblyFilter('');
    setPanchayathFilter('');
    setStatusFilter('');
    setCategoryFilter('');
  };

  // Export to Excel: Replace assembly id with assembly name
  const handleExportExcel = () => {
    const exportData = sortedComplaints.map(complaint => {
      const assemblyObj = filterAssemblies.find(a => a._id === complaint.legislativeAssembly);
      return {
        ...complaint,
        legislativeAssembly: assemblyObj ? assemblyObj.name : complaint.legislativeAssembly
      };
    });
    const worksheetComplaints = XLSX.utils.json_to_sheet(exportData);
    
    const criteriaReport = [
      { 'Criteria': 'Search Query', 'Value': searchQuery || 'N/A' },
      { 'Criteria': 'Start Date', 'Value': startDate || 'N/A' },
      { 'Criteria': 'End Date', 'Value': endDate || 'N/A' },
      { 'Criteria': 'Assembly Filter', 'Value': assemblyFilter ? (filterAssemblies.find(a => a._id === assemblyFilter)?.name || assemblyFilter) : 'N/A' },
      { 'Criteria': 'Panchayath Filter', 'Value': panchayathFilter || 'N/A' },
      { 'Criteria': 'Status Filter', 'Value': statusFilter || 'N/A' },
      { 'Criteria': 'Category Filter', 'Value': categoryFilter || 'N/A' },
    ];
    const worksheetCriteria = XLSX.utils.json_to_sheet(criteriaReport);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheetComplaints, 'Complaints');
    XLSX.utils.book_append_sheet(workbook, worksheetCriteria, 'Search Criteria');

    const currentDate = new Date();
    const fileName = `complaints_export_${currentDate.getFullYear()}${(currentDate.getMonth() + 1)
      .toString().padStart(2, '0')}${currentDate.getDate().toString().padStart(2, '0')}_${
      currentDate.getHours().toString().padStart(2, '0')}${currentDate.getMinutes().toString().padStart(2, '0')}${currentDate.getSeconds().toString().padStart(2, '0')}.xlsx`;

    XLSX.writeFile(workbook, fileName);
  };

  // Export to JSON: Replace assembly id with assembly name
  const handleExportJSON = () => {
    const exportData = sortedComplaints.map(complaint => {
      const assemblyObj = filterAssemblies.find(a => a._id === complaint.legislativeAssembly);
      return {
        ...complaint,
        legislativeAssembly: assemblyObj ? assemblyObj.name : complaint.legislativeAssembly
      };
    });
    const dataStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `complaints_export_${new Date().toISOString()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Import JSON: Persist imported data via the new endpoint
  const handleImportJSON = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const json = JSON.parse(evt.target.result);
        await api.post('/complaints/import', json);
        alert('Complaints imported successfully to DB!');
        fetchComplaints();
      } catch (err) {
        alert('Failed to import JSON file.');
      }
    };
    reader.readAsText(file);
  };

  const triggerImportJSON = () => {
    if (jsonFileInputRef.current) {
      jsonFileInputRef.current.click();
    }
  };

  return (
    <Container maxWidth={false} sx={{ width: '100%', mt: 7, p: 2, bgcolor: 'var(--bg-color)' }}>
      <Box sx={{ mb: 2 }}>
        {/* Filter Toggle Button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" align="left" sx={{ color: 'var(--text-color)', fontWeight: 600 }}>
            Pending Complaints
          </Typography>
          <Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
            <IconButton
              onClick={() => setShowFilters(!showFilters)}
              color="primary"
              aria-label={showFilters ? "Hide Filters" : "Show Filters"}
              sx={{ 
                border: '1px solid',
                borderColor: 'primary.main',
                borderRadius: 1
              }}
            >
              {showFilters ? <FilterListOffIcon /> : <FilterListIcon />}
            </IconButton>
          </Tooltip>
        </Box>
        <Divider sx={{ mb: 2 }} />
        
        {/* Collapsible Filter Controls */}
        <Collapse in={showFilters}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Search Complaints"
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                fullWidth={isMobile || isTablet}
                size={isMobile ? "small" : "medium"}
                inputProps={{ 'aria-label': 'Search Complaints' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Start Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                size={isMobile ? "small" : "medium"}
                inputProps={{ 'aria-label': 'Start Date' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="End Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
                size={isMobile ? "small" : "medium"}
                inputProps={{ 'aria-label': 'End Date' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl variant="outlined" fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel id="assembly-filter-label">Assembly</InputLabel>
                <Select
                  labelId="assembly-filter-label"
                  label="Assembly"
                  value={assemblyFilter}
                  onChange={(e) => {
                    setAssemblyFilter(e.target.value);
                    setPanchayathFilter('');
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {filterAssemblies.map((assembly) => (
                    <MenuItem key={assembly._id} value={assembly._id}>
                      {assembly.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl variant="outlined" fullWidth disabled={!assemblyFilter} size={isMobile ? "small" : "medium"}>
                <InputLabel id="panchayath-filter-label">Panchayath</InputLabel>
                <Select
                  labelId="panchayath-filter-label"
                  label="Panchayath"
                  value={panchayathFilter}
                  onChange={(e) => setPanchayathFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {assemblyFilter &&
                    filterAssemblies.find((a) => a._id === assemblyFilter)
                      ?.municipalities.map((muni, index) => (
                        <MenuItem key={index} value={muni.name}>
                          {muni.name}
                        </MenuItem>
                      ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl variant="outlined" fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  label="Category"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat._id} value={cat.name}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button 
                variant="outlined" 
                onClick={handleResetFilters} 
                fullWidth={isMobile || isTablet}
                size={isMobile ? "small" : "medium"}
                aria-label="Reset Filters"
              >
                Reset
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <Tooltip title="Export to Excel">
                <IconButton color="primary" onClick={handleExportExcel} aria-label="Export to Excel">
                  <GetAppIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Collapse>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress aria-label="Loading complaints" />
        </Box>
      ) : (
        <>
          {selectedComplaints.length > 0 && (
            <Box sx={{ mb: 2, textAlign: 'right' }}>
              <Tooltip title="Delete Selected Complaints">
                <Button variant="contained" color="error" onClick={handleBulkDelete} aria-label="Bulk Delete">
                  Delete Selected ({selectedComplaints.length})
                </Button>
              </Tooltip>
            </Box>
          )}

          {(isMobile || isTablet) ? (
            // Mobile/Tablet Card View - Show Name and Subject prominently
            <Box>
              {paginatedComplaints.map((complaint) => {
                const assemblyObj = filterAssemblies.find(a => a._id === complaint.legislativeAssembly);
                const assemblyName = assemblyObj ? assemblyObj.name : complaint.legislativeAssembly;
                const categoryName = complaint.category || 'N/A';
                
                const formatDate = (dateString) => {
                  try {
                    if (!dateString) return 'N/A';
                    const date = new Date(dateString);
                    if (isNaN(date.getTime())) return 'Invalid Date';
                    return date.toLocaleString('en-IN', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    });
                  } catch (error) {
                    return 'Invalid Date';
                  }
                };
                
                return (
                  <Card 
                    key={complaint._id}
                    sx={{ 
                      mb: 2, 
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 3 }
                    }}
                    onClick={() => navigate(`/complaint/${complaint._id}`)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {complaint.name}
                          </Typography>
                          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                            {complaint.subject}
                          </Typography>
                        </Box>
                        <Checkbox
                          checked={selectedComplaints.includes(complaint._id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectComplaint(complaint._id, e);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                        <Chip 
                          label={complaint.status} 
                          size="small"
                          sx={{ backgroundColor: getStatusColor(complaint.status), color: 'white', fontWeight: 600 }}
                        />
                        <Chip label={assemblyName} size="small" variant="outlined" />
                        <Chip label={categoryName} size="small" variant="outlined" />
                      </Box>
                      {complaint.mobileNumber && (
                        <Box sx={{ mb: 1, p: 1, backgroundColor: alpha(theme.palette.primary.main, 0.03), borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                            Mobile:
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontWeight: 500 }}>
                              {complaint.mobileNumber}
                            </Typography>
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="Call">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(`tel:${complaint.mobileNumber}`, '_self');
                                  }}
                                  sx={{
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    '&:hover': {
                                      backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                    },
                                    border: '1px solid',
                                    borderColor: alpha(theme.palette.primary.main, 0.3)
                                  }}
                                >
                                  <CallIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="WhatsApp">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(`https://wa.me/${complaint.mobileNumber.replace(/[^0-9]/g, '')}`, '_blank');
                                  }}
                                  sx={{
                                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                                    '&:hover': {
                                      backgroundColor: alpha(theme.palette.success.main, 0.2),
                                    },
                                    border: '1px solid',
                                    borderColor: alpha(theme.palette.success.main, 0.3)
                                  }}
                                >
                                  <WhatsAppIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Box>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(complaint.complaintDate)}
                        </Typography>
                        <Box>
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/complaint/${complaint._id}`);
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/complaints/edit/${complaint._id}`);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
              {sortedComplaints.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No complaints found.
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            // Desktop Table View
            <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 1, overflow: 'hidden' }}>
              <Table>
                <TableHead sx={{ bgcolor: 'var(--primary-color)' }}>
                  <TableRow>
                    <TableCell sx={{ color: '#fff', width: 50 }}>
                      <Checkbox
                        color="default"
                        onChange={handleSelectAll}
                        checked={
                          paginatedComplaints.length > 0 &&
                          paginatedComplaints.every(c => selectedComplaints.includes(c._id))
                        }
                        inputProps={{ 'aria-label': 'Select All Complaints' }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: '#fff', ...cellStyle }}>Name</TableCell>
                    <TableCell sx={{ color: '#fff', ...cellStyle }}>Subject</TableCell>
                    <TableCell sx={{ color: '#fff', ...cellStyle }}>Status</TableCell>
                    <TableCell sx={{ color: '#fff', ...cellStyle }}>Assembly</TableCell>
                    <TableCell sx={{ color: '#fff', ...cellStyle }}>Category</TableCell>
                    <TableCell sx={{ color: '#fff', ...cellStyle }}>Mobile</TableCell>
                    <TableCell sx={{ color: '#fff', ...cellStyle }}>Remarks</TableCell>
                    <TableCell sx={{ color: '#fff', ...cellStyle }}>Date</TableCell>
                    <TableCell align="center" sx={{ color: '#fff' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedComplaints.map((complaint) => {
                    const assemblyObj = filterAssemblies.find(a => a._id === complaint.legislativeAssembly);
                    const assemblyName = assemblyObj ? assemblyObj.name : complaint.legislativeAssembly;
                    return (
                      <TableRow
                        key={complaint._id}
                        onClick={() => navigate(`/complaint/${complaint._id}`)}
                        sx={{
                          "&:hover": {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                            cursor: 'pointer',
                            transition: 'background-color 0.3s'
                          }
                        }}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedComplaints.includes(complaint._id)}
                            onChange={(e) => handleSelectComplaint(complaint._id, e)}
                            inputProps={{ 'aria-label': `Select complaint ${complaint.complaintId}` }}
                          />
                        </TableCell>
                        <TableCell sx={cellStyle}>{complaint.name}</TableCell>
                        <TableCell sx={cellStyle}>{complaint.subject}</TableCell>
                        <TableCell sx={cellStyle}>
                          <Chip 
                            label={complaint.status} 
                            sx={{ backgroundColor: getStatusColor(complaint.status), color: 'white', fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          <Tooltip title={`Panchayath: ${complaint.panchayath || 'N/A'}`} arrow>
                            <span>{assemblyName}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={cellStyle}>{complaint.category}</TableCell>
                        <TableCell sx={cellStyle}>{complaint.mobileNumber}</TableCell>
                        <TableCell sx={cellStyle}>
                          {complaint.remarks ? (
                            <Tooltip title={complaint.remarks} arrow>
                              <IconButton size="small" aria-label="View remarks">
                                <ChatBubbleOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : '–'}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {new Date(complaint.complaintDate).toLocaleString()}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()} align="center">
                          <Tooltip title="View Complaint">
                            <IconButton
                              color="primary"
                              onClick={() => navigate(`/complaint/${complaint._id}`)}
                              aria-label="View Complaint"
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Complaint">
                            <IconButton
                              color="secondary"
                              onClick={() => navigate(`/complaints/edit/${complaint._id}`)}
                              aria-label="Edit Complaint"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {sortedComplaints.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        No complaints found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={sortedComplaints.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
        </>
      )}
    </Container>
  );
};

export default PendingComplaints;
